import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import {
  BookingStatus,
  CleaningLevel,
  ServiceSiteKind,
  ServiceType,
} from '../../../__generated__/graphql';
import { authStore } from '../../../store/authStore';
import { modalActions } from '../../../store/authStore/modalStore';
import { Button } from '../../components/button';
import { CardSetupForm } from '../../components/CardSetupForm';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Field,
  FormGrid,
  InlineRow,
  Input,
  LoadingBlock,
  Muted,
  PageContainer,
  Select,
  Stack,
  Strong,
  Textarea,
} from '../../components/ui';
import { useStripePromise } from '../../hooks/useStripePromise';
import { useBookingsClosedPrompt } from '../../hooks/useBookingsOpen';
import { BookingsClosedModal } from '../../components/BookingsClosedModal';
import { CheckoutTerms } from '../../components/CheckoutTerms';
import { useCheckoutPolicies } from '../../hooks/useCheckoutPolicies';
import {
  isPolicyVersionOutdated,
  outdatedPolicyMessage,
} from '../../services/policyService/checkoutPolicies';
import {
  useCreateJobIntentMutation,
  useMyServiceSitesQuery,
  useServiceJobAsCustomerQuery,
  useServiceWorkerQuery,
  useSyncServiceJobPaymentMutation,
  useWorkerQuoteQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import {
  useCreateRentalPaymentIntentMutation,
  useCreateSetupIntentForBookingMutation,
} from '../../services/stripeService/__generated__/queries.generated';
import { describeCheckoutPaymentError } from '../GuestCarPage/CheckoutPage/checkoutPaymentError';
import {
  CLEANING_LEVEL_DESCRIPTIONS,
  CLEANING_LEVEL_LABELS,
  formatMoney,
  SERVICE_TYPE_LABELS,
  type CleaningLevelKey,
} from '../ServiceProviderPage/priceTables';
import { CheckRow } from '../ServiceProviderPage/serviceControls';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { QuoteBreakdown } from './ServiceGuestComponents';
import {
  handoverCapText,
  holdTimingText,
  jobItems,
  jobPath,
  servicesForSite,
  suppliesWarnings,
  UNINSURED_NOTE,
  vehicleClassOf,
  type ServiceJob,
} from './serviceJob';
import {
  MAX_HANDOVER_HOURS,
  parseServiceSearch,
  serviceSearchParams,
  windowSummary,
  workerPath,
} from './serviceSearchState';
import { SiteFormModal } from './SiteFormModal';
import { siteSummary } from './serviceSiteForm';

const Columns = styled.div`
  ${tw`grid grid-cols-1 gap-8 lg:grid-cols-3`}
`;

const Main = styled.div`
  ${tw`flex flex-col gap-6 lg:col-span-2 min-w-0`}
`;

const Side = styled.aside`
  ${tw`flex flex-col gap-4 min-w-0`}
  @media (min-width: 1024px) {
    position: sticky;
    top: 96px;
    align-self: start;
  }
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold sm:text-3xl`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
`;

/** Minutes a PENDING job holds the worker before it is released (D35). */
const PENDING_HOLD_MINUTES = 30;

/**
 * Book a job (spec 019 W15) at `/services/book/:workerId?…` with the search in
 * the URL. Signed out, the page asks to sign in and stays here (W13). The
 * server prices again and creates the job (`createJobIntent`); the card goes
 * through the spec 013 checkout: a SetupIntent with the name on the card, then
 * `createRentalPaymentIntent`, which holds the amount now (job within 5 days)
 * or only keeps the card (`card_saved`). A bank check ends with
 * `syncServiceJobPayment`.
 */
export default function ServiceBookPage() {
  const { workerId = '', bookingId: routeBookingId } = useParams<{
    workerId: string;
    bookingId?: string;
  }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSnapshot(authStore);
  const state = useMemo(() => parseServiceSearch(params), [params]);

  const workerQuery = useServiceWorkerQuery({ variables: { carId: workerId }, skip: !workerId });
  const car = workerQuery.data?.serviceWorker ?? null;
  const worker = car?.serviceWorker ?? null;
  const sitesQuery = useMyServiceSitesQuery({
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });
  const sites = sitesQuery.data?.myServiceSites ?? [];

  const [siteId, setSiteId] = useState<string>(state.siteId ?? '');
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([state.serviceType]);
  const [cleaningLevel, setCleaningLevel] = useState<CleaningLevel>(state.cleaningLevel);
  const [handoverHours, setHandoverHours] = useState(state.handoverHours);
  const [sameDay, setSameDay] = useState(state.sameDayTurnover);
  const [addOns, setAddOns] = useState<Record<string, number>>({});
  const [accessOverride, setAccessOverride] = useState('');
  const [note, setNote] = useState('');
  const [addingSite, setAddingSite] = useState<ServiceSiteKind | null>(null);
  const [intent, setIntent] = useState<ServiceJob | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [cardKey, setCardKey] = useState(0);
  const [paying, setPaying] = useState(false);
  // Spec 023: "Bookings open soon" when the site is not taking bookings yet.
  const bookingsClosed = useBookingsClosedPrompt();
  const policies = useCheckoutPolicies('service');
  const [termsChecked, setTermsChecked] = useState(false);

  const existing = useServiceJobAsCustomerQuery({
    variables: { id: routeBookingId ?? '' },
    skip: !routeBookingId || !isAuthenticated,
    fetchPolicy: 'network-only',
  });
  const [createJobIntent, { loading: creating }] = useCreateJobIntentMutation();
  const [createSetupIntent] = useCreateSetupIntentForBookingMutation();
  const [createRentalPaymentIntent] = useCreateRentalPaymentIntentMutation();
  const [syncPayment] = useSyncServiceJobPaymentMutation();
  const { stripePromise } = useStripePromise();

  useEffect(() => {
    const booking = existing.data?.getBookingAsGuest;
    if (!booking || intent) return;
    if (booking.status === BookingStatus.Pending || booking.status === BookingStatus.CardDeclined) {
      setIntent(booking);
    } else {
      navigate(jobPath(booking.id), { replace: true });
    }
  }, [existing.data, intent, navigate]);

  const wantedKind =
    state.serviceType === ServiceType.HomeTurnover ? ServiceSiteKind.Home : ServiceSiteKind.Vehicle;
  const fittingSites = sites.filter(s => s.isActive && s.kind === wantedKind);
  const site = fittingSites.find(s => s.id === siteId) ?? null;

  // Pick the only fitting site, or the one from the search.
  useEffect(() => {
    if (!siteId && fittingSites.length === 1) setSiteId(fittingSites[0].id);
  }, [fittingSites, siteId]);

  const offered = (worker?.offerings ?? []).filter(o => o.isActive);
  const kindTypes = servicesForSite(wantedKind).filter(type =>
    offered.some(o => o.serviceType === type),
  );
  const pickedOfferings = offered.filter(o => serviceTypes.includes(o.serviceType));
  const addOnChoices = pickedOfferings.flatMap(o => o.addOns);
  const items = worker
    ? jobItems(offered, {
        serviceTypes,
        homeSize: site ? ((site.homeDetails as any) ?? null) : null,
        sameDayTurnover: sameDay,
        vehicleClass: site ? vehicleClassOf(site.vehicleDetails) : null,
        cleaningLevel,
        handoverHours,
      })
    : [];
  const addOnInput = Object.entries(addOns)
    .filter(([code, quantity]) => quantity > 0 && addOnChoices.some(a => a.code === code))
    .map(([code, quantity]) => ({ code, quantity }));

  const quote = useWorkerQuoteQuery({
    variables: {
      workerCarId: workerId,
      input: {
        siteId,
        serviceDate: state.date,
        windowStart: state.windowStart,
        windowEnd: state.windowEnd,
        items,
        addOns: addOnInput,
      },
    },
    skip: !isAuthenticated || !siteId || items.length === 0 || Boolean(intent),
    fetchPolicy: 'network-only',
  });

  if (workerQuery.loading && !car) return <LoadingBlock label="Loading" />;
  if (!worker) {
    return (
      <PageContainer width="md">
        <Alert tone="error" title="Worker not found">
          This worker is not taking jobs. <Link to="/">Search again</Link>
        </Alert>
      </PageContainer>
    );
  }

  const handover = serviceTypes.includes(ServiceType.VehicleHandover);
  const warnings =
    site && site.kind === ServiceSiteKind.Home
      ? suppliesWarnings(worker.suppliesProvided, site.homeSupplies as any)
      : [];
  const backTo = workerPath(worker.carId, state);

  const toggleType = (type: ServiceType, on: boolean) =>
    setServiceTypes(previous =>
      on ? [...previous.filter(t => t !== type), type] : previous.filter(t => t !== type),
    );

  const startCheckout = async () => {
    if (!isAuthenticated) {
      modalActions.openSignInModal(() => undefined);
      return;
    }
    if (!siteId) {
      setIntentError(
        `Pick the ${wantedKind === ServiceSiteKind.Home ? 'home' : 'vehicle'} for this job.`,
      );
      return;
    }
    if (!items.length) {
      setIntentError('Pick at least one service.');
      return;
    }
    setIntentError(null);
    try {
      const { data } = await createJobIntent({
        variables: {
          input: {
            workerCarId: worker.carId,
            siteId,
            serviceDate: state.date,
            windowStart: state.windowStart,
            windowEnd: state.windowEnd,
            items,
            addOns: addOnInput,
            accessOverride: accessOverride.trim() || null,
            customerNote: note.trim() || null,
            platform: 'web',
            linkedBookingId: state.linkedBookingId ?? null,
          },
        },
      });
      const booking = data?.createJobIntent.booking;
      if (!booking) throw new Error('The job could not be started');
      setIntent(booking);
      navigate(
        `/services/book/${worker.carId}/${booking.id}?${serviceSearchParams({ ...state, siteId }).toString()}`,
        {
          replace: true,
        },
      );
    } catch (e) {
      setIntentError(stayErrorMessage(e, 'The job could not be booked. Try again.'));
    }
  };

  const pay = async ({ paymentMethodId }: { paymentMethodId: string }) => {
    if (!intent) return;
    setPaying(true);
    try {
      const { data } = await createRentalPaymentIntent({
        variables: { input: { bookingId: intent.id, paymentMethodId } },
      });
      const payment = data?.createRentalPaymentIntent;
      if (!payment) throw new Error('The payment could not be started');
      let status = payment.booking?.status ?? null;
      if (payment.requiresAction && payment.clientSecret) {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Payments are not ready yet. Please try again.');
        const { error } = await stripe.confirmCardPayment(payment.clientSecret);
        if (error) throw new Error(error.message || 'The bank did not approve this hold.');
        const synced = await syncPayment({ variables: { bookingId: intent.id } });
        status = synced.data?.syncServiceJobPayment.status ?? status;
      }
      toast.success(
        status === BookingStatus.Requested
          ? 'Request sent. The worker confirms it soon; you are not charged if they do not.'
          : 'Your job is booked',
      );
      navigate(jobPath(intent.id), { replace: true });
    } catch (e) {
      const described = describeCheckoutPaymentError(e, 'The payment did not go through');
      if (described.bookingsClosed) {
        bookingsClosed.show(described.message);
      } else {
        toast.error(described.message, { autoClose: 8000 });
      }
      setCardKey(key => key + 1);
    } finally {
      setPaying(false);
    }
  };

  const shownQuote = quote.data?.workerQuote ?? null;
  const intentReceipt = intent?.receipt;

  return (
    <PageContainer width="xl" data-testid="service-book">
      <Stack $gap={6}>
        <Muted $size="sm">
          <Link to={backTo}>Back to {worker.displayName}</Link>
        </Muted>
        <Title>{worker.instantBook ? 'Confirm and pay' : 'Request this worker'}</Title>
        <Columns>
          <Main>
            {!isAuthenticated ? (
              <Alert tone="info" title="Sign in to book">
                <Stack $gap={4}>
                  <span>
                    Your search stays here. After you sign in, pick your home or vehicle and pay.
                  </span>
                  <div>
                    <Button
                      type="button"
                      text="Sign in"
                      onClick={() => modalActions.openSignInModal(() => undefined)}
                      dataTestId="service-book-sign-in"
                    />
                  </div>
                </Stack>
              </Alert>
            ) : (
              <>
                <Card>
                  <CardHeader
                    title={wantedKind === ServiceSiteKind.Home ? 'Which home?' : 'Which vehicle?'}
                  />
                  <CardBody>
                    <Stack $gap={4}>
                      {fittingSites.length > 0 && (
                        <Select
                          aria-label="Home or vehicle"
                          value={siteId}
                          disabled={Boolean(intent)}
                          onChange={e => setSiteId(e.target.value)}
                          data-testid="service-book-site"
                        >
                          <option value="">Pick one</option>
                          {fittingSites.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.nickname} · {siteSummary(s)} · {s.city} {s.zip}
                            </option>
                          ))}
                        </Select>
                      )}
                      {!intent && (
                        <div>
                          <Button
                            type="button"
                            theme="secondary"
                            size="sm"
                            text={
                              wantedKind === ServiceSiteKind.Home ? 'Add a home' : 'Add a vehicle'
                            }
                            onClick={() => setAddingSite(wantedKind)}
                          />
                        </div>
                      )}
                    </Stack>
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader title="Services" description={windowSummary(state)} />
                  <CardBody>
                    <Stack $gap={4}>
                      {state.linkedBookingId && (
                        <Alert tone="info" title="For your car trip">
                          The worker hands over your car for this trip. Add a cleaning too if you
                          like.
                        </Alert>
                      )}
                      {kindTypes.map(type => (
                        <CheckRow
                          key={type}
                          label={SERVICE_TYPE_LABELS[type]}
                          checked={serviceTypes.includes(type)}
                          onChange={on => !intent && toggleType(type, on)}
                        />
                      ))}
                      {serviceTypes.includes(ServiceType.HomeTurnover) && (
                        <CheckRow
                          label="Same-day turnover"
                          checked={sameDay}
                          onChange={v => !intent && setSameDay(v)}
                        />
                      )}
                      <FormGrid $cols={2}>
                        {serviceTypes.includes(ServiceType.VehicleCleaning) && (
                          <Field
                            label="Clean"
                            htmlFor="book-level"
                            hint={
                              CLEANING_LEVEL_DESCRIPTIONS[
                                cleaningLevel.toLowerCase() as CleaningLevelKey
                              ]
                            }
                          >
                            <Select
                              id="book-level"
                              value={cleaningLevel}
                              disabled={Boolean(intent)}
                              onChange={e => setCleaningLevel(e.target.value as CleaningLevel)}
                            >
                              {Object.values(CleaningLevel).map(l => (
                                <option key={l} value={l}>
                                  {CLEANING_LEVEL_LABELS[l.toLowerCase() as CleaningLevelKey]}
                                </option>
                              ))}
                            </Select>
                          </Field>
                        )}
                        {handover && (
                          <Field
                            label="Handover: bill up to"
                            htmlFor="book-hours"
                            hint={handoverCapText(handoverHours)}
                          >
                            <Select
                              id="book-hours"
                              value={String(handoverHours)}
                              disabled={Boolean(intent)}
                              onChange={e => setHandoverHours(Number(e.target.value))}
                            >
                              {Array.from({ length: MAX_HANDOVER_HOURS }, (_, i) => i + 1).map(
                                h => (
                                  <option key={h} value={h}>
                                    {h} {h === 1 ? 'hour' : 'hours'}
                                  </option>
                                ),
                              )}
                            </Select>
                          </Field>
                        )}
                      </FormGrid>
                      {addOnChoices.length > 0 && (
                        <Stack $gap={4}>
                          <Strong>Add-ons</Strong>
                          {addOnChoices.map(a =>
                            a.unit === 'per_item' ? (
                              <InlineRow key={a.code} style={{ justifyContent: 'space-between' }}>
                                <span>
                                  {a.label} ({formatMoney(a.price)} each)
                                </span>
                                <Input
                                  aria-label={`${a.label} quantity`}
                                  type="number"
                                  min={0}
                                  max={20}
                                  disabled={Boolean(intent)}
                                  value={addOns[a.code] ?? 0}
                                  onChange={e =>
                                    setAddOns(v => ({
                                      ...v,
                                      [a.code]: Math.max(
                                        0,
                                        Math.min(20, Number(e.target.value) || 0),
                                      ),
                                    }))
                                  }
                                  style={{ width: 80 }}
                                />
                              </InlineRow>
                            ) : (
                              <CheckRow
                                key={a.code}
                                label={`${a.label} (${formatMoney(a.price)})`}
                                checked={(addOns[a.code] ?? 0) > 0}
                                onChange={on =>
                                  !intent && setAddOns(v => ({ ...v, [a.code]: on ? 1 : 0 }))
                                }
                              />
                            ),
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader title="For the worker" />
                  <CardBody>
                    <Stack $gap={4}>
                      <Field
                        label="How to get in, for this job only"
                        htmlFor="book-access"
                        hint="Optional. Replaces the saved entry details for this job. The worker sees it 2 hours before."
                      >
                        <Textarea
                          id="book-access"
                          rows={2}
                          maxLength={2000}
                          disabled={Boolean(intent)}
                          value={accessOverride}
                          onChange={e => setAccessOverride(e.target.value)}
                        />
                      </Field>
                      <Field
                        label="Note"
                        htmlFor="book-note"
                        hint={
                          handover
                            ? 'For example, the name of the person picking up the car.'
                            : 'Optional.'
                        }
                      >
                        <Textarea
                          id="book-note"
                          rows={2}
                          maxLength={1000}
                          disabled={Boolean(intent)}
                          value={note}
                          onChange={e => setNote(e.target.value)}
                          data-testid="book-note"
                        />
                      </Field>
                    </Stack>
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader title="Pay" />
                  <CardBody>
                    <Stack $gap={4}>
                      {intentError && (
                        <Alert tone="error" title="Cannot book">
                          {intentError}
                        </Alert>
                      )}
                      <Muted $size="sm">{holdTimingText(state.date)}</Muted>
                      {!intent ? (
                        <Button
                          type="button"
                          text="Continue to payment"
                          size="lg"
                          isLoading={creating}
                          isDisabled={!shownQuote || Boolean(quote.error)}
                          onClick={startCheckout}
                          dataTestId="service-book-continue"
                        />
                      ) : (
                        <>
                          <Muted $size="sm">
                            We keep this time for you for {PENDING_HOLD_MINUTES} minutes while you
                            pay.
                          </Muted>
                          <CheckoutTerms
                            checked={termsChecked}
                            outdatedMessage={policies.outdatedMessage}
                            onChange={checked => {
                              setTermsChecked(checked);
                              policies.clearOutdated();
                            }}
                          />
                          {termsChecked && (
                            <CardSetupForm
                              key={`${intent.id}:${cardKey}`}
                              loadSetup={async () => {
                                try {
                                  await policies.accept(intent.id);
                                } catch (error) {
                                  if (isPolicyVersionOutdated(error)) {
                                    setTermsChecked(false);
                                    throw new Error(outdatedPolicyMessage(error));
                                  }
                                  throw error;
                                }
                                const open = await bookingsClosed.beforeConfirm();
                                if (!open) throw new Error('This site is not taking bookings yet.');
                                const { data } = await createSetupIntent({
                                  variables: { bookingId: intent.id },
                                });
                                const setup = data?.createSetupIntentForBooking;
                                if (!setup?.clientSecret)
                                  throw new Error('The card form could not be opened.');
                                return {
                                  clientSecret: setup.clientSecret,
                                  customerSessionClientSecret: setup.customerSessionClientSecret,
                                };
                              }}
                              onCardReady={pay}
                              beforeConfirm={bookingsClosed.beforeConfirm}
                              busy={paying}
                              submitLabel={worker.instantBook ? 'Book and hold' : 'Send request'}
                              notice="You are charged after the job is done and you confirm it, or automatically 24 hours later."
                            />
                          )}
                        </>
                      )}
                    </Stack>
                  </CardBody>
                </Card>
              </>
            )}
          </Main>
          <Side>
            <Card>
              <CardHeader
                title={worker.displayName}
                description={`${worker.provider.businessName} · ${worker.city} ${worker.zip}`}
              />
              <CardBody>
                <Stack $gap={4}>
                  {!worker.provider.insuranceVerified && (
                    <Alert tone="warning" title="Not insured" data-testid="service-book-uninsured">
                      {UNINSURED_NOTE}
                    </Alert>
                  )}
                  {warnings.map(w => (
                    <Muted key={w} $size="sm" data-testid="service-book-supplies">
                      {w}
                    </Muted>
                  ))}
                  {intent && intentReceipt?.authorizedAmount != null ? (
                    <Stack $gap={4}>
                      <Strong>Total {formatMoney(Number(intentReceipt.totalPrice ?? 0))}</Strong>
                      <Muted $size="sm">
                        We hold {formatMoney(Number(intentReceipt.authorizedAmount))}
                        {intentReceipt.addOnAllowance
                          ? `, of which up to ${formatMoney(Number(intentReceipt.addOnAllowance))} covers add-ons you approve on site`
                          : ''}
                        .
                      </Muted>
                    </Stack>
                  ) : !isAuthenticated || !siteId ? (
                    <Muted $size="sm">
                      Pick your {wantedKind === ServiceSiteKind.Home ? 'home' : 'vehicle'} to see
                      the price.
                    </Muted>
                  ) : quote.loading && !shownQuote ? (
                    <LoadingBlock label="Pricing" />
                  ) : quote.error ? (
                    <Alert tone="warning" title="Cannot book this">
                      {stayErrorMessage(quote.error)}
                    </Alert>
                  ) : shownQuote ? (
                    <QuoteBreakdown
                      quote={shownQuote}
                      handoverCapHours={handover ? handoverHours : null}
                    />
                  ) : null}
                </Stack>
              </CardBody>
            </Card>
          </Side>
        </Columns>
      </Stack>
      {addingSite && (
        <SiteFormModal
          kind={addingSite}
          onClose={() => setAddingSite(null)}
          onSaved={saved => setSiteId(saved.id)}
        />
      )}
      <BookingsClosedModal message={bookingsClosed.message} onClose={bookingsClosed.close} />
    </PageContainer>
  );
}
